import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { useSite } from "@/lib/useSite";
import { imgUrl } from "@/lib/api";
import Chapter from "@/components/Chapter";

function BeforeAfter({ pair }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="gallery-before-after-slider"
      className="relative aspect-[16/10] w-full cursor-ew-resize select-none overflow-hidden rounded-[2rem] bg-ink shadow-[0_30px_70px_-32px_rgba(13,13,17,0.45)]"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
    >
      <img src={imgUrl(pair.after)} alt={`${pair.title} — after`} className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      <img
        src={imgUrl(pair.before)}
        alt={`${pair.title} — before`}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable="false"
      />
      <div className="absolute inset-y-0 w-px bg-white" style={{ left: `${pos}%` }}>
        <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-lg">
          <MoveHorizontal size={18} />
        </span>
      </div>
      <span className="absolute left-4 top-4 bg-ink/70 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white">Before</span>
      <span className="absolute right-4 top-4 bg-srs px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white">After</span>
    </div>
  );
}

export default function Gallery() {
  const { data } = useSite();
  const items = data?.gallery || [];
  const photos = items.filter((g) => g.type !== "pair" && g.image);
  const pair = items.find((g) => g.type === "pair" && g.before && g.after);

  if (!items.length) return null;

  return (
    <div>
      <Chapter
        no="04"
        kicker="Portfolio"
        heading="Selected work."
        sub="A few recent installations across Mandya district."
      />
      {pair && (
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12"
        >
          <BeforeAfter pair={pair} />
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-subtle">{pair.title} — drag to compare</p>
        </motion.div>
      )}
      <div className="grid grid-cols-2 gap-5 md:grid-cols-3">
        {photos.map((g, i) => (
          <motion.figure
            key={g.id || i}
            data-testid={`gallery-item-${i}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.07, ease: [0.16, 1, 0.3, 1] }}
            className="group relative overflow-hidden rounded-3xl bg-ink shadow-[0_18px_45px_-22px_rgba(29,29,31,0.28)] transition-shadow duration-500 hover:shadow-[0_30px_65px_-24px_rgba(29,29,31,0.4)]"
          >
            <img
              src={imgUrl(g.image)}
              alt={g.title}
              loading="lazy"
              className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            />
            <figcaption className="absolute inset-x-0 bottom-0 translate-y-2 bg-ink/80 px-4 py-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
              <span className="block truncate text-sm font-medium text-white">{g.title}</span>
              {g.tag && <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-srs">{g.tag}</span>}
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  );
}
